<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import Auth from 'dashboard/api/auth';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();
const iframeRef = ref(null);

const accountId = computed(() => route.params.accountId);

const iframeSrc = computed(() => {
  const templateManagerUrl = window.chatwootConfig?.templateManagerUrl;
  if (!templateManagerUrl) return '';

  const authData = Auth.getAuthData() || {};
  const params = new URLSearchParams({
    account_id: accountId.value,
    inbox_id: props.inbox.id,
    access_token: authData['access-token'] || '',
    token_type: authData['token-type'] || '',
    client: authData.client || '',
    expiry: authData.expiry || '',
    uid: authData.uid || '',
  });

  return `${templateManagerUrl}/?${params.toString()}`;
});

function getCurrentTheme() {
  return document.body.classList.contains('dark') ? 'dark' : 'light';
}

function sendThemeToIframe() {
  iframeRef.value?.contentWindow?.postMessage(
    { type: 'theme-change', theme: getCurrentTheme() },
    '*'
  );
}

function onMessage(event) {
  if (event.data?.type === 'theme-request') {
    sendThemeToIframe();
  }
}

let observer;

onMounted(() => {
  window.addEventListener('message', onMessage);
  // Watch for dark class changes on body to push updates to iframe
  observer = new MutationObserver(sendThemeToIframe);
  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onBeforeUnmount(() => {
  window.removeEventListener('message', onMessage);
  observer?.disconnect();
});
</script>

<template>
  <div class="w-full h-full bg-n-surface-1 rounded-2xl overflow-hidden">
    <iframe
      v-if="iframeSrc"
      ref="iframeRef"
      :src="iframeSrc"
      :title="$t('INBOX_MGMT.TEMPLATES.IFRAME_TITLE')"
      class="w-full h-full border-0"
      allow="clipboard-write"
      @load="sendThemeToIframe"
    />
    <div v-else class="flex items-center justify-center h-full text-n-slate-11">
      {{ $t('INBOX_MGMT.TEMPLATES.MANAGER_NOT_CONFIGURED') }}
    </div>
  </div>
</template>
